import { EventEmitter } from 'node:events';
import type { Server as HttpServer } from 'node:http';
import WebSocket, { WebSocketServer } from 'ws';
import { metrics } from '../metrics.js';

/** Configuration for the streaming server. */
export interface StreamingServerOptions {
  /** HTTP server to mount the WebSocket on. */
  server: HttpServer;
  /** Recording engine (EventEmitter with session-start, session-end, action-recorded events). */
  engine: EventEmitter & {
    activeSessionId: string | null;
    isRecording: boolean;
  };
  /** Maximum frames per second to send to each client. */
  maxFps: number;
}

/** Per-client state for frame throttling. */
interface ClientState {
  ws: WebSocket;
  lastFrameSentAt: number;
}

/**
 * WebSocket server for live-streaming browser sessions.
 *
 * Mounts on the provided HTTP server at /ws/live.
 * Broadcasts screencast frames, action events, and session lifecycle
 * notifications to all connected clients.
 *
 * Message protocol (server → client):
 * - { type: 'idle' } — no active session
 * - { type: 'session_start', sessionId: string }
 * - { type: 'session_end', sessionId: string }
 * - { type: 'frame', data: string } — base64 JPEG
 * - { type: 'action', seq, tool, args, timestamp, pageUrl, pageTitle }
 */
export class StreamingServer {
  private readonly wss: WebSocketServer;
  private readonly clients = new Set<ClientState>();
  private readonly engine: StreamingServerOptions['engine'];
  private readonly frameIntervalMs: number;
  private latestFrame: Buffer | null = null;

  constructor(options: StreamingServerOptions) {
    this.engine = options.engine;
    this.frameIntervalMs = Math.floor(1000 / options.maxFps);

    this.wss = new WebSocketServer({
      server: options.server,
      path: '/ws/live',
    });

    this.wss.on('connection', (ws) => this.handleConnection(ws));

    // Listen for engine events
    this.engine.on('session-start', (data: { sessionId: string }) => {
      this.broadcast({ type: 'session_start', sessionId: data.sessionId });
    });

    this.engine.on(
      'session-end',
      (data: { sessionId: string }) => {
        this.broadcast({ type: 'session_end', sessionId: data.sessionId });
      },
    );

    this.engine.on(
      'action-recorded',
      (data: {
        sessionId: string;
        action: {
          seq: number;
          tool: string;
          args: Record<string, unknown>;
          timestamp: string;
          pageUrl: string;
          pageTitle: string;
        };
      }) => {
        this.broadcast({
          type: 'action',
          seq: data.action.seq,
          tool: data.action.tool,
          args: data.action.args,
          timestamp: data.action.timestamp,
          pageUrl: data.action.pageUrl,
          pageTitle: data.action.pageTitle,
        });
      },
    );
  }

  /** Set the latest frame (used to send to newly connected clients). */
  setLatestFrame(frame: Buffer): void {
    this.latestFrame = frame;
  }

  /** Broadcast a screencast frame to all clients, respecting per-client throttle. */
  broadcastFrame(frame: Buffer): void {
    this.latestFrame = frame;
    const now = Date.now();
    const base64 = frame.toString('base64');
    const msg = JSON.stringify({ type: 'frame', data: base64 });

    for (const client of this.clients) {
      if (client.ws.readyState !== WebSocket.OPEN) continue;
      if (now - client.lastFrameSentAt < this.frameIntervalMs) continue;

      client.lastFrameSentAt = now;
      client.ws.send(msg);
    }
  }

  /** Number of connected clients. */
  get clientCount(): number {
    return this.clients.size;
  }

  /** Shut down the WebSocket server and close all connections. */
  shutdown(): void {
    for (const client of this.clients) {
      client.ws.close();
    }
    this.clients.clear();
    this.wss.close();
  }

  private handleConnection(ws: WebSocket): void {
    const state: ClientState = { ws, lastFrameSentAt: 0 };
    this.clients.add(state);
    metrics.increment('wsConnectionsActive');
    metrics.increment('wsConnectionsTotal');

    ws.on('close', () => {
      this.clients.delete(state);
      metrics.decrement('wsConnectionsActive');
    });

    ws.on('error', () => {
      this.clients.delete(state);
      metrics.decrement('wsConnectionsActive');
    });

    // Send initial state
    if (this.engine.isRecording && this.engine.activeSessionId) {
      this.send(ws, {
        type: 'session_start',
        sessionId: this.engine.activeSessionId,
      });

      // Send latest frame if we have one
      if (this.latestFrame) {
        this.send(ws, {
          type: 'frame',
          data: this.latestFrame.toString('base64'),
        });
      }
    } else {
      this.send(ws, { type: 'idle' });
    }
  }

  private broadcast(msg: Record<string, unknown>): void {
    const raw = JSON.stringify(msg);
    for (const client of this.clients) {
      if (client.ws.readyState === WebSocket.OPEN) {
        client.ws.send(raw);
      }
    }
  }

  private send(ws: WebSocket, msg: Record<string, unknown>): void {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }
}
